import { useEffect } from 'react';

/**
 * ScrollLag
 * ---------
 * Adds a subtle scroll-linked lag to cover-style editorial wrappers
 * so the site feels editorial and floating rather than 1:1-locked to
 * the wheel. Not a fixed-pixel parallax on the image inside its frame
 * — the wrapper itself translates by a small offset derived from the
 * live delta between the document's scroll position and a smoothed
 * follower of it. When the document accelerates, the follower lags,
 * producing a positive offset; when scroll stops, the follower catches
 * up and the offset returns to zero. Symmetric in both directions.
 *
 * Exclusions (mirrored in the CSS rule):
 *   • [data-testid="hero-section"]     — hero stays perfectly static
 *   • .img-wrap--natural               — artworks stay perfectly static
 *   • body.admin-scope                 — admin UI untouched
 *   • <iframe>                         — never (we only tag div.img-wrap)
 *
 * Guardrails:
 *   • Wrapper-only transform — no scale, no object-position, no
 *     size/margin/padding changes; the wrapper's overflow: hidden
 *     keeps the image cropped exactly as before, so no background
 *     edge is ever revealed and no visible cropping shifts.
 *   • MAX_LAG kept modest so blocks glide within the natural gap
 *     between editorial sections and cannot overlap neighbours.
 *   • Reduced-motion users see no movement (see index.css).
 */

const MAX_LAG = 22;           // px absolute cap on the lag offset
const LAG_STRENGTH = 0.55;    // multiplier applied to the raw delta
const SMOOTH = 0.11;          // per-frame follower easing (0..1)
const REDUCED_MOTION_Q = '(prefers-reduced-motion: reduce)';

function qualifies(el) {
    if (!(el instanceof Element)) return false;
    if (!el.classList.contains('img-wrap')) return false;
    if (el.classList.contains('img-wrap--natural')) return false;
    if (el.closest('[data-testid="hero-section"]')) return false;
    if (el.closest('.admin-scope')) return false;
    return true;
}

export default function ScrollLag() {
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (window.matchMedia && window.matchMedia(REDUCED_MOTION_Q).matches) return;

        // Restrict per-frame work to elements currently near/in the viewport.
        const visible = new Set();
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) visible.add(e.target);
                    else {
                        visible.delete(e.target);
                        // Snap-back so an off-screen element doesn't reappear mid-lag.
                        e.target.style.setProperty('--lag', '0px');
                    }
                }
            },
            { rootMargin: '30% 0px 30% 0px' },
        );

        const tag = (el) => {
            if (!qualifies(el) || el.classList.contains('scroll-lag')) return;
            el.classList.add('scroll-lag');
            io.observe(el);
        };

        document.querySelectorAll('.img-wrap').forEach(tag);

        // Watch for route changes / lazy hydration adding new wrappers.
        const mo = new MutationObserver((mutations) => {
            for (const m of mutations) {
                for (const node of m.addedNodes) {
                    if (!(node instanceof Element)) continue;
                    if (node.classList?.contains('img-wrap')) tag(node);
                    else node.querySelectorAll?.('.img-wrap').forEach(tag);
                }
            }
        });
        mo.observe(document.body, { childList: true, subtree: true });

        // The follower state is a single scalar shared across all visible
        // wrappers — the effect is uniform, only its rendering is scoped.
        let follower = window.scrollY;
        let raf = null;
        const loop = () => {
            const docY = window.scrollY;
            follower += (docY - follower) * SMOOTH;
            const delta = docY - follower;
            let lag = delta * LAG_STRENGTH;
            if (lag > MAX_LAG) lag = MAX_LAG;
            else if (lag < -MAX_LAG) lag = -MAX_LAG;
            const lagStr = `${lag.toFixed(2)}px`;
            if (visible.size > 0) {
                visible.forEach((el) => el.style.setProperty('--lag', lagStr));
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            mo.disconnect();
            io.disconnect();
            document.querySelectorAll('.scroll-lag').forEach((el) => {
                el.classList.remove('scroll-lag');
                el.style.removeProperty('--lag');
            });
        };
    }, []);

    return null;
}
